import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePresentacionCliente } from '../../context/PresentacionClienteContext';
import {
  CHANNEL_PREFIX,
  resolveFotoUrl,
  usePresentacionChannel,
} from '../../presentacion/presentacionClienteShared';
import { usePresentacionPropiedadData } from '../../presentacion/usePresentacionPropiedadData';
import { videosPlayables } from '../../presentacion/presentacionVideo';

function precioTextoFromMeta(meta) {
  if (meta.precio_web_consultar) return 'Consultar';
  if (meta.precio) return `${Number(meta.precio).toLocaleString('es-ES')} €`;
  return '';
}

function btnMini(disabled) {
  return {
    padding: '4px 10px',
    borderRadius: 6,
    border: '1px solid rgba(148,163,184,0.35)',
    background: disabled ? 'rgba(51,65,85,0.4)' : 'rgba(56,189,248,0.18)',
    color: '#e2e8f0',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 11,
  };
}

function posicionPorDefecto() {
  const panelW = 300;
  const estH = Math.min(420, window.innerHeight - 100);
  return {
    left: Math.max(8, window.innerWidth - panelW - 16),
    top: Math.max(8, window.innerHeight - estH - 16),
  };
}

function clampPos(left, top, panelEl) {
  const w = panelEl?.offsetWidth ?? 300;
  const h = panelEl?.offsetHeight ?? 380;
  const maxL = Math.max(8, window.innerWidth - w - 8);
  const maxT = Math.max(8, window.innerHeight - h - 8);
  return {
    left: Math.min(maxL, Math.max(8, left)),
    top: Math.min(maxT, Math.max(8, top)),
  };
}

export default function PresentacionMandoFlotante() {
  const { sesion, cerrarMando } = usePresentacionCliente();
  const propiedadId = sesion?.propiedadId ?? null;
  const canal = sesion?.canal ?? '';

  const { fotos, videos, meta, cargando, error } = usePresentacionPropiedadData(propiedadId);
  const [index, setIndex] = useState(0);
  const videosOk = useMemo(() => videosPlayables(videos), [videos]);
  const [emitiendoVideo, setEmitiendoVideo] = useState(false);
  const [videoIdx, setVideoIdx] = useState(0);
  const safeVidIdx = videosOk.length ? Math.min(videoIdx, videosOk.length - 1) : 0;

  const chRef = useRef(null);
  const panelRef = useRef(null);
  const dragRef = useRef({ startX: 0, startY: 0, origLeft: 0, origTop: 0 });
  const [panelPos, setPanelPos] = useState(() => posicionPorDefecto());
  const [arrastrando, setArrastrando] = useState(false);

  useLayoutEffect(() => {
    if (!sesion) return;
    setPanelPos(posicionPorDefecto());
  }, [sesion?.canal, sesion?.propiedadId]);

  useEffect(() => {
    if (!sesion) return undefined;
    const onResize = () => {
      setPanelPos((p) => clampPos(p.left, p.top, panelRef.current));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [sesion]);

  const onBarraPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: panelPos.left,
      origTop: panelPos.top,
    };
    setArrastrando(true);
  }, [panelPos.left, panelPos.top]);

  const onBarraPointerMove = useCallback(
    (e) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
      const d = dragRef.current;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      setPanelPos(clampPos(d.origLeft + dx, d.origTop + dy, panelRef.current));
    },
    []
  );

  const onBarraPointerUp = useCallback((e) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setArrastrando(false);
  }, []);

  useEffect(() => {
    if (!canal) return undefined;
    chRef.current = new BroadcastChannel(CHANNEL_PREFIX + canal);
    return () => {
      chRef.current?.close();
      chRef.current = null;
    };
  }, [canal]);

  const enviarGoto = useCallback((i) => {
    try {
      chRef.current?.postMessage({ type: 'goto', index: i });
    } catch {
      /* ignorar */
    }
  }, []);

  const enviarVideoMode = useCallback((payload) => {
    try {
      chRef.current?.postMessage({ type: 'videoMode', ...payload });
    } catch {
      /* ignorar */
    }
  }, []);

  const urls = fotos.map((f) => resolveFotoUrl(f)).filter(Boolean);
  const safeIndex = urls.length ? Math.min(index, urls.length - 1) : 0;
  const url = urls[safeIndex];
  const precioTexto = precioTextoFromMeta(meta);

  const goto = useCallback(
    (i) => {
      setEmitiendoVideo(false);
      if (!urls.length) return;
      const n = Math.max(0, Math.min(i, urls.length - 1));
      setIndex(n);
      enviarGoto(n);
    },
    [urls.length, enviarGoto]
  );

  const onGoto = useCallback(
    (i) => {
      if (!urls.length) return;
      const n = Math.max(0, Math.min(i, urls.length - 1));
      setIndex(n);
    },
    [urls.length]
  );

  usePresentacionChannel(canal, onGoto);

  useEffect(() => {
    setIndex(0);
    setVideoIdx(0);
    setEmitiendoVideo(false);
  }, [propiedadId]);

  const toggleVideoEnTv = useCallback(() => {
    if (videosOk.length === 0) return;
    if (emitiendoVideo) {
      enviarVideoMode({ show: false });
      setEmitiendoVideo(false);
    } else {
      enviarVideoMode({ show: true, videoIndex: safeVidIdx });
      setEmitiendoVideo(true);
    }
  }, [videosOk.length, emitiendoVideo, enviarVideoMode, safeVidIdx]);

  const pasarVideo = useCallback(
    (delta) => {
      setVideoIdx((i) => {
        const max = Math.max(0, videosOk.length - 1);
        const n = Math.max(0, Math.min(max, i + delta));
        if (emitiendoVideo) {
          enviarVideoMode({ show: true, videoIndex: n });
        }
        return n;
      });
    },
    [videosOk.length, emitiendoVideo, enviarVideoMode]
  );

  const cerrarMandoYVisorTv = useCallback(() => {
    if (canal) {
      try {
        const ch = new BroadcastChannel(CHANNEL_PREFIX + canal);
        ch.postMessage({ type: 'cerrarVisor' });
        ch.close();
      } catch {
        /* */
      }
    }
    cerrarMando();
  }, [canal, cerrarMando]);

  if (!sesion) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Mando vista cliente"
      style={{
        position: 'fixed',
        zIndex: 99990,
        left: panelPos.left,
        top: panelPos.top,
        width: 300,
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'min(420px, calc(100vh - 100px))',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        border: '1px solid rgba(148,163,184,0.3)',
        borderRadius: 12,
        boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
        fontFamily: "system-ui, 'Segoe UI', sans-serif",
        overflow: 'hidden',
      }}
    >
      <div
        onPointerDown={onBarraPointerDown}
        onPointerMove={onBarraPointerMove}
        onPointerUp={onBarraPointerUp}
        onPointerCancel={onBarraPointerUp}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '8px 10px',
          borderBottom: '1px solid rgba(148,163,184,0.2)',
          flexShrink: 0,
          cursor: arrastrando ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
        }}
        title="Arrastra para mover el mando"
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', flex: 1, minWidth: 0 }}>
          Vista cliente
        </span>
        <button
          type="button"
          onClick={cerrarMandoYVisorTv}
          onPointerDown={(e) => e.stopPropagation()}
          title="Cerrar mando y la ventana de la TV"
          style={{
            border: 'none',
            background: 'rgba(239,68,68,0.2)',
            color: '#fecaca',
            width: 26,
            height: 26,
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 16,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      <div style={{ padding: 8, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {cargando && (
          <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>Cargando…</p>
        )}
        {error && (
          <p style={{ margin: 0, fontSize: 11, color: '#fecaca' }}>{error}</p>
        )}
        {!cargando && !error && (
          <>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" style={btnMini(safeIndex <= 0)} onClick={() => goto(safeIndex - 1)} disabled={safeIndex <= 0}>
                ←
              </button>
              <button
                type="button"
                style={btnMini(safeIndex >= urls.length - 1)}
                onClick={() => goto(safeIndex + 1)}
                disabled={safeIndex >= urls.length - 1}
              >
                →
              </button>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>
                {urls.length ? `${safeIndex + 1} / ${urls.length}` : '—'}
              </span>

              {videosOk.length > 0 && (
                <>
                  <span style={{ width: 1, height: 14, background: 'rgba(148,163,184,0.35)', margin: '0 2px' }} aria-hidden />
                  {videosOk.length > 1 && (
                    <button
                      type="button"
                      style={btnMini(safeVidIdx <= 0)}
                      onClick={() => pasarVideo(-1)}
                      disabled={safeVidIdx <= 0}
                      title="Vídeo anterior"
                    >
                      ‹
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={toggleVideoEnTv}
                    title={
                      emitiendoVideo
                        ? 'Volver a fotos en la TV'
                        : `Emitir vídeo en la TV${videosOk.length > 1 ? ` (${safeVidIdx + 1}/${videosOk.length})` : ''}`
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 30,
                      height: 26,
                      padding: 0,
                      borderRadius: 6,
                      border: emitiendoVideo ? '2px solid #38bdf8' : '1px solid rgba(148,163,184,0.35)',
                      background: emitiendoVideo ? 'rgba(56,189,248,0.25)' : 'rgba(56,189,248,0.12)',
                      cursor: 'pointer',
                      color: '#e2e8f0',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M4 6.5C4 5.67 4.67 5 5.5 5h7c.83 0 1.5.67 1.5 1.5v11c0 .83-.67 1.5-1.5 1.5h-7A1.5 1.5 0 014 17.5v-11zM16 8.2l4-2.5v12.6l-4-2.5V8.2z" />
                    </svg>
                  </button>
                  {videosOk.length > 1 && (
                    <button
                      type="button"
                      style={btnMini(safeVidIdx >= videosOk.length - 1)}
                      onClick={() => pasarVideo(1)}
                      disabled={safeVidIdx >= videosOk.length - 1}
                      title="Vídeo siguiente"
                    >
                      ›
                    </button>
                  )}
                </>
              )}
            </div>

            <div
              style={{
                borderRadius: 8,
                overflow: 'hidden',
                background: '#000',
                aspectRatio: '16/9',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {url ? (
                <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: 11, color: '#64748b' }}>Sin fotos</span>
              )}
            </div>

            <p
              style={{
                margin: 0,
                fontSize: 10,
                color: '#cbd5e1',
                lineHeight: 1.35,
                wordBreak: 'break-word',
              }}
            >
              {meta.referencia && <span>Ref. {meta.referencia} · </span>}
              {precioTexto && <span>{precioTexto} · </span>}
              {meta.num_dormitorios > 0 && <span>🛏{meta.num_dormitorios} </span>}
              {meta.num_banos > 0 && <span>🚿{meta.num_banos} </span>}
              {meta.m2_construidos > 0 && <span>📐{Math.round(meta.m2_construidos)}m²</span>}
            </p>

            {urls.length > 1 && (
              <div
                style={{
                  display: 'flex',
                  gap: 4,
                  overflowX: 'auto',
                  flexShrink: 0,
                  paddingBottom: 4,
                  scrollbarWidth: 'thin',
                }}
              >
                {urls.map((u, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goto(i)}
                    style={{
                      padding: 0,
                      border: i === safeIndex ? '2px solid #38bdf8' : '2px solid transparent',
                      borderRadius: 4,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      width: 44,
                      height: 33,
                      flexShrink: 0,
                      background: '#111',
                    }}
                  >
                    <img src={u} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
